import { useState, useEffect } from 'react';
import { ShieldCheck, Search, CheckCircle, XCircle, FileText, ExternalLink, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase';

export interface BusinessClaim {
  id: string;
  listing_id: string;
  listing_name: string;
  claimant_name: string;
  claimant_phone: string;
  claimant_email: string;
  shop_photo: string;
  gst_doc: string;
  bill_doc: string;
  otp_verified: boolean;
  status: 'pending_review' | 'approved' | 'rejected';
  created_at: string;
}

export function ClaimsManager() {
  const [claims, setClaims] = useState<BusinessClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedClaim, setSelectedClaim] = useState<BusinessClaim | null>(null);

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    setLoading(true);
    let dbClaims: BusinessClaim[] = [];
    try {
      const { data } = await supabase.from('business_claims').select('*').order('created_at', { ascending: false });
      if (data && data.length > 0) dbClaims = data as BusinessClaim[];
    } catch {
      // Local fallback
    }

    const localClaims = JSON.parse(localStorage.getItem('localhub_claims') || '[]');
    const combinedMap = new Map<string, BusinessClaim>();
    
    // Seed default sample claim if empty
    if (dbClaims.length === 0 && localClaims.length === 0) {
      const seed: BusinessClaim = {
        id: 'claim-seed-1',
        listing_id: 'sample-biz-chennai-101',
        listing_name: 'Apex Business Services',
        claimant_name: 'Rajesh Kumar',
        claimant_phone: '9876543210',
        claimant_email: 'contact@business.com',
        shop_photo: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80',
        gst_doc: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=800&q=80',
        bill_doc: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?auto=format&fit=crop&w=800&q=80',
        otp_verified: true,
        status: 'pending_review',
        created_at: new Date().toISOString()
      };
      combinedMap.set(seed.id, seed);
    }

    [...dbClaims, ...localClaims].forEach(c => combinedMap.set(c.id, c));
    setClaims(Array.from(combinedMap.values()));
    setLoading(false);
  };

  const handleApprove = async (claim: BusinessClaim) => {
    try {
      await supabase.from('business_claims').update({ status: 'approved' }).eq('id', claim.id);
      await supabase.from('listings').update({ email: claim.claimant_email, phone: claim.claimant_phone }).eq('id', claim.listing_id);
    } catch {
      // Ignore
    }

    const updated = claims.map(c => c.id === claim.id ? { ...c, status: 'approved' as const } : c);
    setClaims(updated);
    localStorage.setItem('localhub_claims', JSON.stringify(updated));
    setSelectedClaim(null);
    alert(`Business claim for "${claim.listing_name}" APPROVED! Ownership transferred to ${claim.claimant_email}.`);
  };

  const handleReject = async (claim: BusinessClaim) => {
    try {
      await supabase.from('business_claims').update({ status: 'rejected' }).eq('id', claim.id);
    } catch {
      // Ignore
    }

    const updated = claims.map(c => c.id === claim.id ? { ...c, status: 'rejected' as const } : c);
    setClaims(updated);
    localStorage.setItem('localhub_claims', JSON.stringify(updated));
    setSelectedClaim(null);
    alert(`Claim for "${claim.listing_name}" rejected.`);
  };

  const filtered = claims.filter(c =>
    c.listing_name.toLowerCase().includes(search.toLowerCase()) ||
    c.claimant_name.toLowerCase().includes(search.toLowerCase()) ||
    c.claimant_email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto text-slate-800 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-amber-500" /> Business Claim Verifications
          </h1>
          <p className="text-xs text-slate-500 font-semibold mt-1">
            Inspect legal proof documents (Shop Photo, GST/MSME Proof, Utility Bill) & grant ownership
          </p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search claims..."
            className="pl-9 pr-4 w-full h-10 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 placeholder:text-slate-400 outline-none focus:border-emerald-600 shadow-sm"
          />
        </div>
      </div>

      {/* Claims Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left border-collapse">
            <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-5 py-3.5">Business Name</th>
                <th className="px-5 py-3.5">Claimant Details</th>
                <th className="px-5 py-3.5">Email OTP</th>
                <th className="px-5 py-3.5">Submitted</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-semibold">
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50/60 transition">
                  <td className="px-5 py-4">
                    <div className="font-bold text-slate-900 text-sm">{c.listing_name}</div>
                    <div className="text-[10px] text-slate-400 font-mono">ID: {c.listing_id}</div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="font-bold text-slate-800">{c.claimant_name}</div>
                    <div className="text-[10px] text-slate-500">{c.claimant_email} · {c.claimant_phone}</div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full">
                      <CheckCircle className="h-3 w-3 text-emerald-600" /> OTP Verified
                    </span>
                  </td>
                  <td className="px-5 py-4 font-mono text-[10px] text-slate-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-slate-400" />
                      {new Date(c.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full uppercase ${
                      c.status === 'approved' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                      c.status === 'rejected' ? 'bg-red-100 text-red-800 border border-red-300' :
                      'bg-amber-100 text-amber-800 border border-amber-300'
                    }`}>
                      {c.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button
                      onClick={() => setSelectedClaim(c)}
                      className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-3.5 py-1.5 rounded-xl shadow-sm transition flex items-center gap-1 ml-auto cursor-pointer"
                    >
                      <FileText className="h-3.5 w-3.5" /> Inspect Proof Docs
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length === 0 && (
          <div className="py-12 text-center text-xs text-slate-400 font-semibold">
            No business claims found matching search criteria.
          </div>
        )}
      </div>

      {/* Document Inspector Modal */}
      {selectedClaim && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedClaim(null)}>
          <div className="bg-white border border-slate-200 rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl space-y-5 text-slate-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-amber-500" /> Claim Proof Document Inspector
                </h3>
                <p className="text-xs text-slate-500 font-semibold mt-0.5">Claim for `{selectedClaim.listing_name}` by {selectedClaim.claimant_name}</p>
              </div>
              <button onClick={() => setSelectedClaim(null)} className="text-slate-400 hover:text-slate-600 font-bold text-lg">✕</button>
            </div>

            {/* Claimant info card */}
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-[10px] text-slate-400 font-bold uppercase">Claimant Name</span>
                <div className="font-bold text-slate-900">{selectedClaim.claimant_name}</div>
              </div>
              <div>
                <span className="text-[10px] text-slate-400 font-bold uppercase">Contact Phone</span>
                <div className="font-bold text-slate-900">{selectedClaim.claimant_phone}</div>
              </div>
              <div>
                <span className="text-[10px] text-slate-400 font-bold uppercase">Official Business Email</span>
                <div className="font-bold text-emerald-700">{selectedClaim.claimant_email}</div>
              </div>
              <div>
                <span className="text-[10px] text-slate-400 font-bold uppercase">OTP Verification</span>
                <div className="font-bold text-emerald-600">Verified ✓</div>
              </div>
            </div>

            {/* Documents preview grid */}
            <div className="space-y-4">
              <div className="font-bold text-xs text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-1">
                Attached Legal Proof Documents (3 Files)
              </div>

              {/* 1. Shop Front Photo */}
              <div className="bg-white border border-slate-200 p-4 rounded-2xl space-y-2">
                <div className="flex justify-between items-center text-xs font-bold text-slate-900">
                  <span>1. Shop Front Photo (Storefront / Signboard)</span>
                  {selectedClaim.shop_photo && (
                    <a href={selectedClaim.shop_photo} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline flex items-center gap-1 text-[11px]">
                      Full Resolution <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                {selectedClaim.shop_photo ? (
                  <img src={selectedClaim.shop_photo} alt="Shop Front" className="w-full h-44 object-cover rounded-xl border border-slate-200 bg-slate-50" />
                ) : (
                  <div className="h-20 bg-slate-50 border border-dashed rounded-xl flex items-center justify-center text-xs text-slate-400">No shop photo attached</div>
                )}
              </div>

              {/* 2. GST / MSME Certificate */}
              <div className="bg-white border border-slate-200 p-4 rounded-2xl space-y-2">
                <div className="flex justify-between items-center text-xs font-bold text-slate-900">
                  <span>2. Legal Business Registration (GST / MSME Proof)</span>
                  {selectedClaim.gst_doc && (
                    <a href={selectedClaim.gst_doc} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline flex items-center gap-1 text-[11px]">
                      Full Resolution <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                {selectedClaim.gst_doc ? (
                  <img src={selectedClaim.gst_doc} alt="GST Proof" className="w-full h-44 object-cover rounded-xl border border-slate-200 bg-slate-50" />
                ) : (
                  <div className="h-20 bg-slate-50 border border-dashed rounded-xl flex items-center justify-center text-xs text-slate-400">No GST document attached</div>
                )}
              </div>

              {/* 3. Official Utility Bill */}
              <div className="bg-white border border-slate-200 p-4 rounded-2xl space-y-2">
                <div className="flex justify-between items-center text-xs font-bold text-slate-900">
                  <span>3. Official Billing Proof (Phone / Internet / EB Bill)</span>
                  {selectedClaim.bill_doc && (
                    <a href={selectedClaim.bill_doc} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline flex items-center gap-1 text-[11px]">
                      Full Resolution <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                {selectedClaim.bill_doc ? (
                  <img src={selectedClaim.bill_doc} alt="Bill Proof" className="w-full h-44 object-cover rounded-xl border border-slate-200 bg-slate-50" />
                ) : (
                  <div className="h-20 bg-slate-50 border border-dashed rounded-xl flex items-center justify-center text-xs text-slate-400">No utility bill attached</div>
                )}
              </div>
            </div>

            {/* Verification decision buttons */}
            <div className="pt-3 flex items-center justify-between border-t border-slate-100">
              <button
                type="button"
                onClick={() => setSelectedClaim(null)}
                className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700"
              >
                Close
              </button>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleReject(selectedClaim)}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition flex items-center gap-1 cursor-pointer"
                >
                  <XCircle className="h-4 w-4" /> Reject Claim
                </button>
                <button
                  type="button"
                  onClick={() => handleApprove(selectedClaim)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-5 py-2.5 rounded-xl transition shadow-md flex items-center gap-1 cursor-pointer"
                >
                  <CheckCircle className="h-4 w-4" /> Approve Claim & Grant Access
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
